import React, { useCallback } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { DraggableDialog } from "../../consts";
import Utils from "../../utils";
import { AuthContext } from "../auth/AuthContext";
import FileDropZone from "./upload-file-drop-zone";
import { useSnackbar } from "../toast";
import { CONVERSATION_UPLOAD_FAILED, CONVERSATION_UPLOAD_SUCCESS } from "../../helpers/messages";
import { useThemeMode } from "../theme";
import moment from "moment";

function UploadDialogue({ open, onClose, campaignData, onSuccess }) {
    const [uploading, setUploading] = React.useState(false);
    const [uploadComplete, setUploadComplete] = React.useState(false);
    const [uploadError, setUploadError] = React.useState(false);
    const [selectedTab] = React.useState("Upload Audio");
    const [files, setFiles] = React.useState([]);
    const [agentsFile, setAgentsFile] = React.useState([]); // For linking agents CSV
    const [agents, setAgents] = React.useState([]); // Parsed agent linking data
    const showSnackbar = useSnackbar();
    const { FONT } = useThemeMode();

    const { isAuthenticated, currentNonAdminGroup, refreshUser } = React.useContext(AuthContext);

    // Parse agents CSV if `agentsFile` changes
    React.useEffect(() => {
        if (agentsFile.length > 0) {
            readAgentFile(agentsFile[0]);
        }
    }, [agentsFile]);

    async function readAgentFile(file) {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = function (e) {
                const contents = e.target.result;
                const list = contents
                    .split("\n")
                    .filter((z) => z.trim() !== "")
                    .map((z) => {
                        const line = z.split(",");
                        return {
                            filename: line[0].trim(),
                            agent: line[1] ? line[1].trim() : "No agent",
                        };
                    });
                setAgents(list);
                resolve(list);
            };
            reader.readAsText(file);
        });
    }

    const uploadAudioHelper = useCallback(async (file) => {
        const result = await Utils.saveToS3(file, { isAuthenticated, currentNonAdminGroup });
        return result.key;
    }, [isAuthenticated, currentNonAdminGroup]);

    const getAgentName = useCallback((fileName) => {
        let agent = "No agent";
        const found = agents.find((z) => z.filename === fileName);
        if (found) agent = found.agent;
        return agent;
    }, [agents])

    const dateDetectorFromFileName = (fileName) => {
        const formats = ["YYYYMMDD", "YYYY-MM-DD", "YYYY/MM/DD", "DD-MM-YYYY", "DD/MM/YYYY"];
        const values = fileName.split("_");
        for (const item of values) {
            const date = moment(item, formats, true);
            if (date.isValid()) {
                // Add current time to it
                const currentTime = moment(); // now
                date.set({
                    hour: currentTime.hour(),
                    minute: currentTime.minute(),
                    second: currentTime.second(),
                    millisecond: currentTime.millisecond()
                });
                return date.toISOString().substring(0, 16).replace("T", " ");
            }
        }
        return new Date().toISOString().substring(0, 16).replace("T", " ");
    }

    const handleDone = async () => {
        if (files.length === 0) return;
        setUploading(true);
        setUploadError(false);

        try {
            // refresh current user and groups
            await refreshUser();

            for (const file of files) {
                try {
                    const key = await uploadAudioHelper(file);
                    const agentName = getAgentName(file.name);
                    const today = dateDetectorFromFileName(file.name);
                    
                    const conversationPayload = {
                        name: key,
                        agent: agentName,
                        file: file.name,
                        startdate: today,
                        symblstatus: "uploaded",
                        enddate: today,
                        campaignConversationsId: campaignData?.id,
                        groups: currentNonAdminGroup,
                    };
                    const conversation = await Utils.genericMutation("createConversation", conversationPayload);

                    // the success hook.
                    onSuccess(conversation.data.createConversation.id);
                    
                    showSnackbar(CONVERSATION_UPLOAD_SUCCESS)

                    // close the modal after success.
                    handleDialogClose();
                } catch (error) {
                    console.error("Error uploading file:", file.name, error);
                    showSnackbar(CONVERSATION_UPLOAD_FAILED, "error")
                    setUploadError(true);
                }
            }
            setUploading(false);
        } catch (error) {
            console.error("Error during upload:", error);
            setUploading(false);
            setUploadError(true);
        }
    };

    const handleCancel = () => {
        setFiles([]);
        setAgentsFile([]);
        setAgents([]);
        setUploading(false);
        setUploadComplete(false);
        setUploadError(false);
    };

    const handleDialogClose = (event, reason) => {
        // Prevent closing the dialog on backdrop click or escape key press
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        // Reset state when closing the dialog after upload is done or error
        setFiles([]);
        setAgentsFile([]);
        setAgents([]);
        setUploading(false);
        setUploadComplete(false);
        setUploadError(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            aria-labelledby="draggable-dialog-title"
            scroll="paper"
            disableEscapeKeyDown={true}
            fullWidth={true}
            maxWidth="sm"
            PaperComponent={DraggableDialog}
            onClick={(e) => e.stopPropagation()}
        >
            <DialogTitle
                id="draggable-dialog-title"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                gap={1}
            >
                Upload Conversation
                <IconButton
                    className="draggable-cancel"
                    size="small"
                    aria-label="close"
                    onClick={handleDialogClose}
                    sx={(theme) => ({
                        color: theme.palette.grey[400],
                        marginRight: "-.5rem",
                    })}
                >
                    <FeatherIcon icon="x" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ paddingY: 3 }}>
                {uploadComplete && !uploadError && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <CheckCircleIcon sx={{ fontSize: 50, color: "#df9508" }} />
                        <Box
                            sx={{
                                mt: 2,
                                fontSize: FONT.SIZE_18,
                                fontWeight: "bold",
                                color: "#000", // Changed to black
                            }}
                        >
                            <p color="#000">Upload Complete !</p>
                        </Box>
                    </Box>
                )}
                {uploadError && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <ErrorIcon sx={{ fontSize: 50, color: "red" }} />
                        <Box
                            sx={{
                                mt: 2,
                                fontSize: FONT.SIZE_18,
                                fontWeight: "bold",
                                color: "#000", // Changed to black
                            }}
                        >
                            <p color="#000">Issue Uploading Content !</p>
                        </Box>
                    </Box>
                )}

                {!uploadComplete && !uploadError && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                        }}
                    >
                        <FileDropZone
                            files={files}
                            setFiles={setFiles}
                            selectedTab={selectedTab}
                        />
                        <Box sx={{ marginTop: 2 }}>
                            <Button
                                onClick={() => {
                                    const link = document.createElement("a");
                                    link.download = `template.csv`;
                                    link.href = "/template.csv";
                                    link.click();
                                }}
                                sx={{
                                    fontWeight: 400,
                                }}
                            >
                                Download template file here
                            </Button>
                        </Box>
                    </Box>
                )}

                {uploading && !uploadComplete && !uploadError && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(0,0,0,0.1)",
                            zIndex: 9999,
                            borderRadius: "12px",
                        }}
                    >
                        <CircularProgress
                            sx={{
                                color: "#df9508",
                            }}
                        />
                    </Box>
                )}
            </DialogContent>
            {!uploadComplete && !uploadError &&
                <DialogActions>
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={handleCancel}
                        disabled={uploading}
                    >
                        Clear
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        onClick={handleDone}
                        disabled={uploading || files.length === 0}
                    >
                        {uploading ? <CircularProgress color="inherit" size={24} /> : "Start Upload"}
                    </Button>
                </DialogActions>
            }
        </Dialog>
    );
}

export default UploadDialogue;
